import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faStore, faTicketAlt, faUserTie,
    faExclamationTriangle, faChartLine, faTrophy,
    faDownload, faSpinner, faCrown
} from '@fortawesome/free-solid-svg-icons';
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Row, Col, Spinner } from 'react-bootstrap';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';
const PIE_COLORS = ['#64748b', '#6366f1', '#a855f7'];

const SuperAdminStats = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);

    useEffect(() => { fetchStats(); }, []);

    const fetchStats = () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/stats', {}, r => {
            if (r.success) setStats(r.data);
            else Notify({ type: 'fail', text: 'Error al cargar métricas globales' });
            setLoading(false);
        }, 'json', () => { setLoading(false); });
    };

    const handleExport = () => {
        setExportLoading(true);
        request.Get(API_BASE + 'superadmin/export/global', {}, r => {
            if (r.success && r.csvData) {
                const blob = new Blob([r.csvData], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.setAttribute('download', `datamanager_global_${new Date().toISOString().split('T')[0]}.csv`);
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } else {
                Notify({ type: 'fail', text: 'No se pudo generar el reporte' });
            }
            setExportLoading(false);
        }, 'json', () => { setExportLoading(false); });
    };

    const TipCustom = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: 10, padding: '10px 14px', fontSize: '0.8rem', boxShadow: 'var(--shadow-md)' }}>
                <p style={{ color: 'var(--text-muted)', margin: '0 0 4px' }}>{label}</p>
                {payload.map((p, i) => (
                    <p key={i} style={{ color: p.color, margin: 0, fontWeight: 700 }}>{p.name}: {p.value}</p>
                ))}
            </div>
        );
    };

    const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        if (percent < 0.05) return null;
        const RAD = Math.PI / 180;
        const r = innerRadius + (outerRadius - innerRadius) * 0.55;
        return (
            <text x={cx + r * Math.cos(-midAngle * RAD)} y={cy + r * Math.sin(-midAngle * RAD)}
                fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    const kpis = stats ? [
        { label: 'Empresas Activas', value: stats.totalCompanies ?? 0, icon: faBuilding, variant: 'sa-blue', sub: 'en la plataforma' },
        { label: 'Sucursales', value: stats.totalBranches ?? 0, icon: faStore, variant: 'sa-green', sub: 'activas' },
        { label: 'Conexiones', value: stats.totalConnections ?? 0, icon: faTicketAlt, variant: 'sa-purple', sub: 'creadas en el sistema' },
        { label: 'Administradores', value: stats.totalAdmins ?? 0, icon: faUserTie, variant: 'sa-amber', sub: 'registrados' },
        { label: 'Módulos Totales', value: stats.totalModules ?? 0, icon: faExclamationTriangle, variant: 'sa-red', sub: 'configurados' },
    ] : [];

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '60vh' }}>
                <div className="text-center">
                    <Spinner animation="grow" style={{ color: 'var(--sa-primary)' }} />
                    <p className="mt-3 text-muted small">Cargando métricas globales...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-in pb-5">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h2 style={{ fontWeight: 800, margin: 0 }}>
                        <FontAwesomeIcon icon={faCrown} style={{ color: 'var(--sa-gold)', marginRight: '0.6rem' }} />
                        Dashboard de Plataforma
                    </h2>
                    <p className="text-muted small mt-1 mb-0">Vista consolidada de toda la actividad de DataManager</p>
                </div>
                <button className="sa-btn sa-btn-ghost" onClick={handleExport} disabled={exportLoading}>
                    <FontAwesomeIcon icon={exportLoading ? faSpinner : faDownload} spin={exportLoading} />
                    Exportar Reporte Global
                </button>
            </div>

            {/* KPI grid */}
            <div className="sa-stats-grid">
                {kpis.map((k, i) => (
                    <motion.div key={i} className={`sa-stat-card ${k.variant}`}
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
                        <div className="sa-stat-icon"><FontAwesomeIcon icon={k.icon} /></div>
                        <div className="sa-stat-info">
                            <p>{k.label}</p>
                            <h3>{k.value}</h3>
                            <small>{k.sub}</small>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Warning strip */}
            {(stats?.trialExpiringSoon ?? 0) > 0 && (
                <div className="sa-alert sa-alert-warning mb-4">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>
                        <strong>{stats.trialExpiringSoon} admin{stats.trialExpiringSoon > 1 ? 's tienen' : ' tiene'} el trial por vencer.</strong>
                        {' '}Revisá el módulo Administradores para extenderlos.
                    </span>
                </div>
            )}

            {/* Charts row 1 */}
            <Row className="g-4 mb-4">
                <Col lg={8}>
                    <motion.div className="sa-panel h-100" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                        <div className="sa-panel-header">
                            <h6 className="sa-panel-title">
                                <FontAwesomeIcon icon={faChartLine} style={{ color: 'var(--sa-primary)' }} />
                                Actividad Global — Últimos 30 días
                            </h6>
                            <span className="sa-badge sa-badge-premium">Consultas totales</span>
                        </div>
                        <div className="sa-panel-body" style={{ height: 280 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={stats?.activityLast30Days || []}>
                                    <defs>
                                        <linearGradient id="saG" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#a855f7" stopOpacity={0.35} />
                                            <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} dy={8} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                                    <Tooltip content={<TipCustom />} />
                                    <Area type="monotone" dataKey="total" name="Consultas" stroke="#a855f7" strokeWidth={2.5} fillOpacity={1} fill="url(#saG)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </motion.div>
                </Col>

                <Col lg={4}>
                    <motion.div className="sa-panel h-100" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                        <div className="sa-panel-header">
                            <h6 className="sa-panel-title">
                                <FontAwesomeIcon icon={faTrophy} style={{ color: 'var(--sa-gold)' }} />
                                Distribución de Planes
                            </h6>
                        </div>
                        <div className="sa-panel-body d-flex flex-column align-items-center" style={{ height: 280 }}>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie data={stats?.planDistribution || []} cx="50%" cy="50%"
                                        outerRadius={80} innerRadius={40} dataKey="count" nameKey="plan"
                                        labelLine={false} label={PieLabel}>
                                        {(stats?.planDistribution || []).map((_, i) => (
                                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<TipCustom />} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="d-flex gap-3 flex-wrap justify-content-center">
                                {(stats?.planDistribution || []).map((p, i) => (
                                    <div key={i} className="d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                                        <div style={{ width: 10, height: 10, borderRadius: 3, background: PIE_COLORS[i] }} />
                                        <span style={{ color: 'var(--text-muted)' }}>{p.plan}</span>
                                        <strong style={{ color: 'var(--text-main)' }}>{p.count}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </Col>
            </Row>

            {/* Company ranking */}
            <motion.div className="sa-panel" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                <div className="sa-panel-header">
                    <h6 className="sa-panel-title">
                        <FontAwesomeIcon icon={faTrophy} style={{ color: 'var(--sa-gold)' }} />
                        Ranking de Empresas — Conexiones y Módulos
                    </h6>
                    <span className="sa-badge sa-badge-warning">Top 10</span>
                </div>
                <div className="sa-panel-body" style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats?.companyRanking || []} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                            <defs>
                                <linearGradient id="barG" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor="#a855f7" />
                                    <stop offset="100%" stopColor="#6366f1" />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border-color)" />
                            <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                            <YAxis type="category" dataKey="empresa" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} width={150} />
                            <Tooltip content={<TipCustom />} />
                            <Bar dataKey="conexiones" name="Conexiones" stackId="a" fill="#a855f7" />
                            <Bar dataKey="modulos" name="Módulos" stackId="a" radius={[0, 6, 6, 0]} fill="#6366f1" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </motion.div>
        </div>
    );
};

export default SuperAdminStats;
